"use client";

import { useEffect, useState } from "react";
import Cropper, { type Area, type Point } from "react-easy-crop";
import { ZoomIn, ZoomOut } from "lucide-react";
import BaseDialog from "@/components/Modals/BaseDialog";
import { Slider } from "@/components/shadcn/slider";
import { SpotToast } from "@/components/SpotToast";
import { cropImageFile, formatDimensions } from "./imageCrop";

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
// How much of the image stays visible around the crop square, per side.
const OVERLAY_MARGIN = 24;

interface ImageCropDialogProps {
  file: File;
  onConfirm: (file: File) => void;
  onUseOriginal: () => void;
  onCancel: () => void;
}

export function ImageCropDialog({
  file,
  onConfirm,
  onUseOriginal,
  onCancel,
}: ImageCropDialogProps) {
  // Created and revoked in the same effect: a memoised URL revoked by an
  // earlier cleanup (StrictMode's double mount) would leave the cropper blank.
  const [src, setSrc] = useState<string>();
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [croppedArea, setCroppedArea] = useState<Area>();
  const [applying, setApplying] = useState(false);

  const ready = Boolean(src && croppedArea && croppedArea.width > 0);

  const handleApply = async () => {
    if (!src || !croppedArea) return;
    setApplying(true);
    try {
      onConfirm(await cropImageFile(file, src, croppedArea));
    } catch (error) {
      console.error(error);
      SpotToast.error("Could not crop the image. Please try again.");
      setApplying(false);
    }
  };

  return (
    <BaseDialog
      isOpen
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      title="Upload image"
      description="Drag to reposition and zoom to resize."
      size="lg"
      footerActions={[
        {
          label: "Use original",
          props: {
            variant: "outline",
            onClick: onUseOriginal,
            disabled: applying,
          },
        },
        {
          label: "Apply",
          props: {
            onClick: handleApply,
            loading: applying,
            disabled: !ready,
          },
        },
      ]}
    >
      <div className="flex flex-col gap-4">
        <div className="relative aspect-square w-full overflow-hidden rounded-md bg-muted">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              aspect={1}
              objectFit="cover"
              showGrid={false}
              style={{
                // The crop box fills the cropper's container, so the container
                // is inset from the frame and its overlay spills into the gap.
                containerStyle: { inset: OVERLAY_MARGIN, overflow: "visible" },
                cropAreaStyle: {
                  boxShadow: "0 0 0 9999em rgba(0, 0, 0, 0.7)",
                },
              }}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setCroppedArea(pixels)}
            />
          )}
        </div>

        <div className="flex items-center gap-3">
          <ZoomOut size={16} className="shrink-0 text-muted-foreground" />
          <Slider
            aria-label="Zoom"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={[zoom]}
            onValueChange={([value]) => setZoom(value)}
          />
          <ZoomIn size={16} className="shrink-0 text-muted-foreground" />
          {ready && croppedArea && (
            <span className="shrink-0 text-xs text-muted-foreground">
              {formatDimensions(croppedArea)}
            </span>
          )}
        </div>
      </div>
    </BaseDialog>
  );
}
